import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GUIDES_BY_SLUG, GUIDES } from "@/data/guidesData";
import { LOGO_URL } from "@/config";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, Calendar } from "lucide-react";

const SITE = "https://creatara.ai";

export default function GuidePage() {
  const { slug } = useParams();
  const guide = GUIDES_BY_SLUG[slug];
  if (!guide) return <Navigate to="/guides" replace />;

  const url = `${SITE}/guides/${guide.slug}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: guide.metaDescription,
    image: `${SITE}/og-default.png`,
    datePublished: guide.publishedAt,
    dateModified: guide.publishedAt,
    author: {
      "@type": "Person",
      name: "Johnson Awosoji",
      url: `${SITE}/about`,
      jobTitle: "Founder, ProJohnson Consulting Ltd",
    },
    publisher: {
      "@type": "Organization",
      name: "Creatara",
      logo: { "@type": "ImageObject", url: LOGO_URL },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: guide.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE },
      { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE}/guides` },
      { "@type": "ListItem", position: 3, name: guide.title, item: url },
    ],
  };

  const related = (guide.relatedSlugs || []).map((s) => GUIDES_BY_SLUG[s]).filter(Boolean);

  return (
    <>
      <Helmet>
        <title>{guide.metaTitle}</title>
        <meta name="description" content={guide.metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={guide.metaTitle} />
        <meta property="og:description" content={guide.metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Creatara" />
        <meta property="article:author" content="Johnson Awosoji" />
        <meta property="article:published_time" content={guide.publishedAt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={guide.metaTitle} />
        <meta name="twitter:description" content={guide.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Nav */}
        <nav className="fixed top-0 w-full z-50 glass">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="guide-logo">
              <img src={LOGO_URL} alt="Creatara" className="w-10 h-10 rounded-lg" />
              <span className="font-bold text-lg">Creatara</span>
            </Link>
            <Link to="/auth"><Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="guide-cta-nav">Try Free</Button></Link>
          </div>
        </nav>

        <article className="max-w-3xl mx-auto px-6 pt-32 pb-20" data-testid={`guide-page-${guide.slug}`}>
          <Link to="/guides" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" /> All guides
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-3 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(guide.publishedAt).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {guide.readMinutes} min read</span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">{guide.title}</h1>

          {/* Answer-first (BLUF) — direct answer in first paragraph */}
          <p className="text-lg leading-relaxed text-muted-foreground bg-white/5 border-l-4 border-violet-500 pl-5 py-4 rounded-r mb-10" data-testid="guide-answer-first">
            {guide.answerFirst}
          </p>

          {/* Sections */}
          {guide.sections.map((s, i) => (
            <section key={i} className="mb-10">
              <h2 className="text-2xl font-bold mb-3">{s.h2}</h2>
              {s.body.split("\n\n").map((p, j) => (
                <p key={j} className="text-base leading-relaxed mb-4 text-muted-foreground whitespace-pre-line">{p}</p>
              ))}
            </section>
          ))}

          {/* Expert quote */}
          {guide.expertQuote && (
            <blockquote className="border-l-4 border-amber-500 pl-5 py-4 bg-amber-500/5 rounded-r my-10">
              <p className="italic text-lg leading-relaxed mb-3">"{guide.expertQuote.text}"</p>
              <footer className="text-sm">
                <span className="font-semibold">{guide.expertQuote.author}</span>
                <span className="text-muted-foreground"> — {guide.expertQuote.title}</span>
              </footer>
            </blockquote>
          )}

          {/* FAQ */}
          <section className="my-10">
            <h2 className="text-2xl font-bold mb-5">Frequently asked questions</h2>
            <div className="space-y-4">
              {guide.faqs.map((f, i) => (
                <details key={i} className="rounded-lg bg-white/5 border border-white/10 p-4" data-testid={`faq-${i}`}>
                  <summary className="font-semibold cursor-pointer">{f.q}</summary>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="my-12 rounded-xl bg-gradient-to-br from-violet-600/20 to-amber-600/10 border border-violet-500/30 p-8 text-center">
            <h3 className="text-2xl font-bold mb-2">Try Creatara free</h3>
            <p className="text-muted-foreground mb-5">AI content + scheduling + affiliate tracking + GEO citation tracker. All in one £29/month tool.</p>
            <Link to="/auth"><Button size="lg" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="guide-cta-bottom">Start free trial →</Button></Link>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <section className="border-t border-white/10 pt-8">
              <h3 className="font-semibold mb-4">Related guides</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/guides/${r.slug}`} className="block rounded-lg bg-white/5 border border-white/10 hover:border-violet-500/40 p-4 transition-colors">
                    <Badge variant="secondary" className="text-[10px] mb-2">{r.readMinutes} min</Badge>
                    <p className="font-medium leading-snug">{r.title}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </div>
    </>
  );
}
